/**
 * Game-save persistence. The whole save is a small JSON blob: the economy
 * (coins/xp), inventories (seeds/stock), the rack count, and the live plantings
 * keyed by pot slot. Growth is TIME-BASED and deterministic — `progress` is
 * recomputed from each planting's `plantedAt` and the wall clock — so we never
 * persist progress itself, only the planting facts; growth "continues while away"
 * for free when the player returns.
 *
 * Storage sits behind a tiny swappable `SaveStore` interface. Today it's
 * localStorage (offline, zero infra). LATER, hosting the app's API on Railway with
 * Postgres means swapping ONLY the store implementation (async load/save of the
 * same `SaveData` per user) — no gameplay code changes. At that point `plantedAt`
 * can become server-authoritative to stop device-clock cheating; for now it's the
 * client wall clock (fine for single-player).
 */

import { sandboxActive, sandboxOwner, SANDBOX_HEADER, SANDBOX_KIND } from "./sandbox";
import { coerceAvatar, type AvatarSpec } from "./avatar";
import type { FlowerId } from "./flowers";
import type { CompostEffectId } from "./compostEffects";
import { coerceComposter, type ComposterState } from "./composter";
import { coerceOrders } from "./robot";
import type { Plot } from "../render/plot";
import { coercePlacements, coercePlot, coerceWater, liftFromRetiredHosts, packCells, startingWaterCells, STARTER_OAK, type GardenPlacement } from "./garden";
import { coerceTerrain, packTerrain, type TerrainType } from "./terrain";
import { coerceDecorPlacements, coerceDecorOwned, placedDecorCount, type DecorPlacement } from "./decor";
import { coerceFences } from "./fence";
import { coerceFishCaught, coerceFishSold, coerceRevealedFish } from "./fishing";
import { carryRollForward, coerceGearRolls, pieceRarity, type GearRoll } from "./gearUpgrade";
import { MAX_RIG_TIER, RIG_SLOT_IDS, rigEquipped, rigItemId, STARTER_RIG_TIERS, type RigSlot } from "./fishingGear";
import { fishLevelInfo } from "./level";
import { migrateXpCurve, XP_CURVE_VERSION } from "./xpCurve";
import { makeRng, type Rng } from "./rng";
import { gameNow, setServerNow } from "./gameClock";
import { LIGHT_COLORS } from "./growLight";
import { EQUIPMENT_IDS } from "./equipment";
import { stripRemovedConditions } from "./automation";

/** Migrate a persisted fishing-gear item id after the "line" rig slot was renamed to "handle"
 *  (line1..line8 → handle1..handle8). Any other id passes through unchanged. */
function migrateGearId(id: string): string {
  const m = /^line(\d+)$/.exec(id);
  return m ? `handle${m[1]}` : id;
}

// ---- RIG UPGRADE-TRACK MIGRATION --------------------------------------------------------------------------
//
// The rig used to be a SHOP: `fishingGearOwned` listed every piece the player had ever bought and
// `fishingGear` picked one of them per slot. It is now an UPGRADE TRACK — each of the four rig slots is a
// single TIER NUMBER of 1..8 (see the UPGRADE TRACK section of fishingGear.ts). `migrateRig` below converts a
// shop-era save into that shape, and it is the part of this file that most needs to be right: these are real
// rigs belonging to real players on main.
//
// THE RULE IS "WHAT THEY PAID FOR", NOT "WHAT THEY WORE". A slot's new tier is the HIGHEST tier the player
// OWNS in that slot, not the tier they happened to have equipped — someone who bought tier 6 and left tier 5
// on the rod paid for 6 and keeps 6. The equipped id still counts (a piece can be equipped without appearing
// in the owned list — the free starters always were), so the tier is the max over owned ∪ equipped, floored
// at 1. A player with nothing lands on tier 1, which is the free starter and is now the FLOOR of the model
// rather than a back-fill: `effectiveEquipped`'s empty-slot substitution becomes unnecessary, not wrong.
//
// WHAT IS DROPPED AND WHAT IS KEPT. The redundant lower-tier rig ids leave `fishingGearOwned` — under the
// track a slot holds exactly one piece, so 31 strictly-worse pieces are not an inventory, they are noise.
// LURE ids stay untouched: lures are a FAMILY gate, still owned-and-swapped, and are not on the track.
//
// gearRolls IS THE SUBTLE ONE. It is keyed by gear item id and holds the stat lines the player has PAID to
// reroll and lock, so a slot that collapses from `rod5` to `rod7` must not simply lose `rod5`'s entry — that
// is deleting purchased progress. Each rig slot's roll is CARRIED FORWARD onto the new tier's item id by
// `carryRollForward`, which re-derives the rarity from the new tier and keeps every existing line's value and
// lock flags; a line count that GROWS (tier 5 rare = 3 lines → tier 7 epic = 4) rolls the new line FRESH.
// Rolls belonging to superseded rig ids are then dropped along with the ids; lure rolls pass through.
//
// IT RUNS ON EVERY LOAD, so it must be IDEMPOTENT rather than one-shot. There is no "already migrated" flag
// in the save: the tiers are not persisted as numbers, they are read back out of `fishingGear`'s item ids
// (`rod5` → 5), which is exactly what the collapse writes. So a second pass over a migrated save re-derives
// the same tiers, re-writes the same ids, and carries each roll forward onto the id it already sits on. The
// one step that is not naturally a no-op is rolling a FRESH stat line when the new rarity adds one — hence
// `migrationRng`, which seeds off the piece so the "fresh" roll is the same value every load. Never make that
// roll unseeded: it would hand the player a different line on every reload.
//
// It runs AFTER the line→handle rename (it reads `handle*` ids), never instead of it, so a pre-rename save
// migrates through both in order.

/**
 * The RNG the migration rolls a fresh stat line with, SEEDED from the piece it is rolling for — never
 * `Math.random`. The migration runs on EVERY load (see above), so an unseeded roll would hand the player a
 * DIFFERENT fourth stat line every time they reloaded. Seeding off the new item id + fishing level makes the
 * whole conversion a pure function of the save, so re-running it is a no-op rather than a slot machine.
 */
function migrationRng(itemId: string, level: number): Rng {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < itemId.length; i++) { h ^= itemId.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; }
  return makeRng((h ^ Math.imul(level + 1, 0x9e3779b1)) >>> 0);
}

/** The tier a rig item id names (`rod5` → 5), or 0 if the id is not a piece of that slot. */
function rigIdTier(id: string, slot: RigSlot): number {
  const m = new RegExp(`^${slot}(\\d+)$`).exec(id);
  if (!m) return 0;
  const t = parseInt(m[1]!, 10);
  return Number.isFinite(t) && t >= 1 && t <= MAX_RIG_TIER ? t : 0;
}

interface MigratedRig {
  /** the equipped map with the four rig slots pinned to their tier's canonical id; the lure passes through.
   *  THIS is where the rig's tiers live — there is no separate persisted tier block. */
  fishingGear: Record<string, string>;
  /** LURE ids only — the rig no longer has an owned-list. */
  fishingGearOwned: string[];
  gearRolls: Record<string, GearRoll>;
}

/**
 * Convert the shop-era gear fields into the upgrade-track shape. `equipped` / `owned` / `rolls` must already
 * have been through `migrateGearId` (the line→handle rename). Runs on every load and is idempotent — see the
 * section header. `fishXp` only feeds the value of a stat line that has to roll FRESH because the new tier's
 * rarity carries more lines than the old one's.
 */
function migrateRig(
  equipped: Record<string, string>,
  owned: string[],
  rolls: Record<string, GearRoll>,
  fishXp: number,
): MigratedRig {
  // the highest tier the player OWNS in each slot — owned ∪ equipped, since the starters were never listed.
  // On an already-migrated save `owned` holds no rig ids at all and this reads the tiers straight back off the
  // equipped ids, which is what makes a second pass a no-op.
  const tiers = { ...STARTER_RIG_TIERS };
  for (const slot of RIG_SLOT_IDS) {
    let best = 1;
    for (const id of owned) best = Math.max(best, rigIdTier(id, slot));
    const eq = equipped[slot];
    if (eq) best = Math.max(best, rigIdTier(eq, slot));
    tiers[slot] = best;
  }

  // the rig slots now read straight off the tiers; anything that isn't a rig slot (the lure) is kept as-is.
  const gear: Record<string, string> = {};
  for (const [k, v] of Object.entries(equipped)) {
    if (!(RIG_SLOT_IDS as string[]).includes(k)) gear[k] = v;
  }
  for (const slot of RIG_SLOT_IDS) gear[slot] = rigItemId(slot, tiers[slot]);

  const isRigId = (id: string): boolean => RIG_SLOT_IDS.some((s) => rigIdTier(id, s) > 0);
  // only lures survive in the owned list (deduped — the shop could list a piece twice across old migrations).
  const ownedOut = [...new Set(owned.filter((id) => !isRigId(id)))];

  // ROLLS: carry each rig slot's roll onto its new item id; keep every non-rig (lure) roll untouched.
  const out: Record<string, GearRoll> = {};
  for (const [id, roll] of Object.entries(rolls)) if (!isRigId(id)) out[id] = roll;
  const level = fishLevelInfo(fishXp).level;
  for (const slot of RIG_SLOT_IDS) {
    const newId = rigItemId(slot, tiers[slot]);
    // the slot's best surviving roll: the new tier's own if it has one, else the highest-tier roll below it
    // (the piece the player was actually upgrading from, and the one their reroll gold went into). On an
    // already-migrated save the new tier's own roll is the only candidate, so this carries it onto itself.
    let from: GearRoll | undefined;
    let fromTier = 0;
    for (const [id, roll] of Object.entries(rolls)) {
      const t = rigIdTier(id, slot);
      if (t > fromTier && t <= tiers[slot]) { from = roll; fromTier = t; }
    }
    if (from) out[newId] = carryRollForward(from, slot, pieceRarity(newId), level, migrationRng(newId, level));
  }

  return { fishingGear: gear, fishingGearOwned: ownedOut, gearRolls: out };
}
import { coerceButterfliesCaught, coerceInsectSold, coerceRevealedBugs } from "./insects";
import { coerceCheckin, freshCheckin } from "./checkin";
import { coerceSubscription, NO_SUBSCRIPTION, type SubscriptionState } from "./subscription";
import { isLiveReadonly } from "./saveMode";
import { saveBlockedByOtherTab } from "./playLock";
import { coerceHeldXp, type HeldXp } from "./kickstarter";
import { coerceBugTalents } from "./bugTalents";
import { coerceGuide, freshGuide, type GuideState } from "./guide/engine";
import { coerceFenceStyles } from "./fenceStyles";
import { addCounts, coerceGrantLedger, ledgerDelta, ledgerHasAny, type GrantLedger } from "./grantLedger";

/** A planting in one pot. `plantedAt` = ms epoch when sown (growth start). `compost` = the compost
 *  BATCH the pot was loaded with (see compost.ts); absent = plain, no bonus. */
export interface SavedPlanting {
  species: FlowerId; plantedAt: number; compost?: StoredCompost;
  /** BANKED grow-progress (effective grow-seconds) + the clock it was last advanced to — so bonus-
   *  saved time survives the bonus turning off, and growth continues correctly while away. */
  grown?: number; lastAt?: number;
  /** grow-light NIGHT-time accrued per colour (seconds) + the dominant tier — the colour with the most
   *  time drives the harvest effect (anti-exploit: can't swap lights right before harvesting). */
  litBy?: Record<string, number>; litTier?: number;
  /** grow-seconds gifted at sowing (the tutorial's first crop) — the picture starts from the seed after them */
  giftedGrown?: number;
}

/** A compost EFFECT as persisted: an effect id + the rolled `amount` (its stat) and the [min,max]
 *  range it was rolled from. */
export interface StoredEffect { type: CompostEffectId; amount: number; min: number; max: number; }
/** A compost BATCH as persisted: just the infused effects (no more quality grade). Mirrors CompostBatch. */
export interface StoredCompost {
  effects: StoredEffect[];
  /** 1–5★ (the scrap composter). Absent on batches from the old composter — `rankOf` derives one. */
  rank?: number;
}

/**
 * The composter's live BARREL (the passive ferment). Absent = open/empty barrel accepting plants.
 *   • `plants` — plant ids currently in the barrel (drive the element totals).
 *   • `sealedAt` — ms epoch the recipe was sealed (ferment start); undefined = still open (adding plants).
 *   • `points` — skill points allocated to each effect (budget = 3 × composter tier), effect id → pts.
 *   • `tier` — the composter tier AT SEAL; the ferment time and the brew use it, so upgrading mid-ferment
 *     changes neither. Absent on an open barrel (and on saves from before it existed → current tier).
 */
export interface StoredBarrel {
  plants: FlowerId[];
  sealedAt?: number;
  /** skill points ALLOCATED to each effect (pushes that effect's range up). The TOTAL budget comes from
   *  the composter tier (3 per tier); this map is how the player has distributed it this barrel. */
  points?: Partial<Record<CompostEffectId, number>>;
  tier?: number;
}

/** The complete persisted game state. `v` lets us migrate old saves safely. */
export interface SaveData {
  v: number;
  /**
   * WHEN THIS SNAPSHOT WAS TAKEN (game clock ms, `gameNow()`). The tie-breaker between the cloud copy and
   * the local mirror on load — see `newerSave()`.
   *
   * It exists because the two copies can legitimately DISAGREE. `save()` writes the local mirror first and
   * treats a failed PUT as "offline, the mirror holds it", so a maintenance window leaves the mirror ahead
   * of the server by every action taken during it. `load()` used to prefer the server unconditionally
   * whenever the GET succeeded and then overwrite the mirror with it, so the first refresh after reconnect
   * threw all of that away silently.
   *
   * GAME clock, not `Date.now()`, so it is on the same timeline as every other stamp in the save
   * (`plantedAt`, `power.atMs`) and a dev running at 100x doesn't produce a save that looks older than the
   * server's. It is a comparison key between two copies of ONE player's save on ONE device, never a
   * trust boundary: the server keeps its own `updated_at` for anything authoritative, and it re-stamps
   * plant times itself. Absent (a pre-existing save from before this field) reads as 0 — oldest possible,
   * so a stamped copy always wins over an unstamped one.
   */
  savedAt?: number;
  coins: number;
  /** PETALS — the premium currency (earned via the daily check-in; spent in the premium shop). Absent = 0. */
  petals?: number;
  /**
   * The running total of Petals the SERVER has debited for buy-with-Petals purchases, as far as this copy
   * knows — its acknowledgement. The server owns the real total and takes any difference off a PUT that is
   * behind it (server/petals.js — the ledger), so a tab opened before a purchase cannot restore the spent
   * Petals by saving its old balance. Carried verbatim from the loaded save; absent = 0.
   */
  petalsDebited?: number;
  /**
   * The COIN → PETAL EXCHANGE's two ledgers (server/exchange.js), acknowledged the same way as
   * `petalsDebited`: the server's running totals of coins it has debited and Petals it has credited, as far
   * as this copy knows. A PUT behind them has the difference applied server-side. Absent = 0.
   */
  coinsDebited?: number;
  petalsCredited?: number;
  /**
   * "server" = this client does NOT pay achievement Petals itself — the server credits them on `petalsCredited` when
   * it first sees the unlock (server/achievementPetals.js). Sent by cloud builds only (a local-only build has no
   * server to pay it); an older build that omits it keeps paying locally and is settled without a credit.
   */
  achPetals?: "server";
  /**
   * THE REDEMPTION LEDGER (server/redeem.js, sim/redeem.ts): the server's running totals of the coins, seeds, decor
   * and achievements codes and admins have granted onto this save, as far as this copy knows — acknowledged the
   * same way as `petalsCredited` (which carries granted Petals). A PUT behind it has the difference added
   * server-side. Absent = nothing granted.
   */
  grantsCredited?: GrantLedger;
  /** DAILY CHECK-IN progress (30-day board). Absent = brand-new (nothing claimed). See sim/checkin.ts. */
  checkin?: import("./checkin").CheckinState;
  /**
   * THE MONTHLY SUBSCRIPTION (sim/subscription.ts) — status + paid-period end. Absent = never subscribed.
   *
   * IT STORES THE ENTITLEMENT AND NOTHING ELSE, which is the point. What a subscription unlocks (3× Petals,
   * claimable achievement rewards) is computed from this field every read; nothing about the player's
   * PROGRESS is stored differently for a subscriber. So the reward rungs an unsubscribed player qualified
   * for are still qualified — qualification is derived from the achievement score and lives nowhere — and
   * the day this field turns `active` they all become claimable at once. Do not add a "rewards forfeited"
   * or "missed" list here: the absence of one is what makes that guarantee structural rather than a promise.
   */
  subscription?: SubscriptionState;
  /** Which LEVEL CURVE the three XP totals below are priced on (sim/xpCurve.ts). Absent = curve 1, the
   *  pre-Oct-2026 curves — such a save is re-expressed on the current curves by `coerce` (and by the server's
   *  `sanitise`), keeping every level and the progress into it. Always written as XP_CURVE_VERSION. */
  xpCurve?: number;
  /** GARDENING xp (the original xp — sales/harvest). Drives the Gardening level. */
  xp: number;
  /** FISHING xp — earned by catching fish. Drives the separate Fishing level. */
  fishXp?: number;
  /** NATURALIST xp — earned by catching insects. Drives the separate Naturalist (bug-catching) level. */
  bugXp?: number;
  /** NATURALIST TALENTS: talent-tier index (0-based, one per 5 levels) → chosen talent id (see sim/bugTalents.ts).
   *  Replaces the old gear-equip bug kit — catching power is level stats + these talent picks. */
  bugTalents?: Record<number, string>;
  /** Owns the EXOTIC PLANT LICENSE — unlocks the fantasy-plant tier in the shop (see sim/level). */
  exoticLicense?: boolean;
  /** Owns the GARDEN HELPER ROBOT — bought once at gardening level 28 (ROBOT_UNLOCK_LEVEL, sim/robot). Also widens the
   *  greenhouse to fit its charging dock, so this field changes the SCENE LAYOUT, not just a capability. */
  robot?: boolean;
  /** The robot's per-pot orders: slotKey → what it may plant/water/harvest there. Empty until the player
   *  assigns work in the terminal, so owning the robot does nothing on its own. */
  robotOrders?: Record<string, { plant?: string; water?: boolean; harvest?: boolean }>;
  /** The robot's SHIFT REQUEST counter (sim/robot.ts). The client raises it to ask for a fresh 4 h shift; the
   *  server stamps a new `robotShiftStart` whenever it goes up. 0 = never asked. */
  robotShiftReq?: number;
  /** When the robot's current shift began (game-clock ms; 0 = never). SERVER-OWNED: `sanitise` sets it from
   *  its own clock and never takes it from a PUT; the client writes it only so the local mirror keeps it. */
  robotShiftStart?: number;
  /** Which interchangeable parts the robot is wearing (sim keeps the ids; the art resolves them). Absent =
   *  the default loadout. RESERVED for the customisation system the parts registry exists to support: no UI
   *  sets it yet, but `App.snapshot()` writes it and the server's `sanitise` keeps it (a string-valued record,
   *  like `potSkins`), so a loadout survives the round trip from the day a UI first sets one. */
  robotLoadout?: Record<string, string>;
  /** The player has been TOLD the Garden Helper exists. Set the first time they hit the unlock level, so the
   *  announcement fires exactly once and the greenhouse's "new gear" callout stops nagging afterwards. The
   *  robot used to sell itself from a button in the corner of the screen; now that it lives on the terminal's
   *  Gear tab, something has to point at the terminal or a capstone purchase can go unnoticed forever. */
  robotAnnounced?: boolean;
  seeds: Record<string, number>;
  stock: Record<string, number>;
  /** COMPOST BIN — brewed compost batches the player holds, ready to load into a pot. Each is a unique
   *  { effects } (recipes differ, so they don't stack into one). */
  compostBin?: StoredCompost[];
  /** The composter's live BARREL — plants added + (once sealed) the ferment timer + earned skill points.
   *  Absent = empty/open barrel. See StoredBarrel. */
  compostBarrel?: StoredBarrel;
  /** COMPOST LOADED INTO A POT, keyed by pot slot key. Persists on the pot across plantings until
   *  emptied; whatever is planted next ADOPTS this batch for its grow. */
  potCompost?: Record<string, StoredCompost>;
  /** THE SCRAP COMPOSTER (sim/composter.ts): ON/OFF, scraps in storage, the batch in progress, the shelf of
   *  finished composts, and its bad-luck counters. Replaces `compostBin` / `compostBarrel`, which are read once
   *  to migrate and written back empty. */
  composter?: ComposterState;
  /** POT GADGETS — pot slot keys that have a GROWTH TIMER device (shows time-to-maturity). */
  potTimers?: string[];
  /** The subset of `potTimers` the player has SWITCHED OFF from the pot dialog: still owned, but no clock on
   *  the pot face and no power draw. Absent/empty = every placed timer is on. */
  timersOff?: string[];
  /** PER-POT SOIL MOISTURE checkpoint (slotKey → { level, at, drySince? }); recomputed from the clock on
   *  load. `drySince` dates the pot's current dry spell — it drives the THIRSTY pulse (sim/moisture). */
  potMoisture?: Record<string, { level: number; at: number; drySince?: number }>;
  rackCount: number;
  /** slotKey → planting */
  plantings: Record<string, SavedPlanting>;
  /** equipmentId → count owned (the compost machine). Only ids in EQUIPMENT_IDS survive a load. */
  equipment: Record<string, number>;
  /** The compost machine's UPGRADE tier (0 = base Compost Bin). Upgrades in place. */
  compostTier?: number;
  /** POWER POLE: whether the player has switched ON grid electricity (the pole powers the greenhouse directly;
   *  billed a flat daily rate). Default off. */
  gridPower?: boolean;
  /** Real-ms timestamp the grid-power meter last billed through. Persisted so AWAY TIME is billed: the
   *  first tick after a reload charges standing+usage for the whole gap (capped in App.tsx), which is what
   *  the moisture / growth sims already do across the same gap. Missing on a pre-existing save =
   *  start the meter from now (no retroactive bill for a gap that was never metered). This replaced
   *  `lastBilledDay`, which was written and saved every tick but never read by anything. */
  lastPowerAccrualMs?: number;
  /** Accrued (unpaid) power BILL in coins. Power is suspended once it exceeds POWER_OVERDUE_LIMIT. */
  powerBill?: number;
  /** Prepaid power CREDIT in coins — each day's charge draws from this before the bill grows. */
  powerCredit?: number;
  /** Whether the daily power charge is AUTO-PAID from coins (after credit). */
  autopay?: boolean;
  /** Placed grow lights, keyed "rack-tier" (one per shelf). */
  growLights?: string[];
  /** Per-light UPGRADE tier, keyed "rack-tier" (missing = tier 0). */
  growLightTiers?: Record<string, number>;
  /** Per-light chosen COLOUR id, keyed "rack-tier" (missing = the tier's first colour). */
  growLightColors?: Record<string, string>;
  /** Per-light automation rules, keyed "rack-tier" (the computer terminal). */
  lightRules?: Record<string, unknown>;
  /** Per-light MANUAL on/off switch, keyed "rack-tier" (default off). Used when a
   *  light has no automation conditions; clicking a light toggles this. */
  lightManual?: Record<string, boolean>;
  /** Placed auto-watering sprinkler PAIRS, keyed "rack-tier" (one pair per shelf). */
  sprinklers?: string[];
  /** Per-sprinkler-shelf automation rules, keyed "rack-tier". Sprinklers have no
   *  manual switch — they run only when their rule passes (and the grid is live). */
  sprinklerRules?: Record<string, unknown>;
  /** Per-sprinkler-shelf SETTINGS from the console's Sprinklers screen, keyed "rack-tier": how long each
   *  activation sprays (`runMin`, real minutes, 0 = until its conditions stop holding). */
  sprinklerSettings?: Record<string, { runMin: number }>;
  /** The iso GARDEN plot — the set of owned cells (expands as the player buys more). */
  plot?: Plot;
  /** Harvested plants placed as decoration in the garden (free positions). */
  garden?: GardenPlacement[];
  /** Bought DECOR the player owns (decorId → count), and each placed decor instance in the garden. */
  decorOwned?: Record<string, number>;
  decor?: DecorPlacement[];
  /** Set once the starting OAK has been placed as a real decor item (was formerly a hardcoded scene tree).
   *  Guards the one-time migration in `coerce` so a player who later sells/moves it doesn't get it re-added. */
  starterOak?: boolean;
  /** FENCES placed on the soil-grid edges (keys "sx,sy,h|v"); see sim/fence.ts. */
  fences?: string[];
  /** Each fence segment's STYLE where it is not the default "rail" (edge key → style id; sim/fenceStyles.ts).
   *  Only non-default segments are stored, so a save of plain fences carries nothing here. */
  fenceStyles?: Record<string, string>;
  /** Player-painted WATER cells (keys "wx,wy" on the fine water sub-grid). In memory only — the WIRE form
   *  is a packed row-run string (`packForWire`); `coerce` reads either. */
  gardenWater?: string[];
  /** Player-painted TERRAIN cells (cellKey "wx,wy" → type: sand/dirt/pebble/stone) on the same sub-grid.
   *  Packed per type on the wire, like `gardenWater`. */
  gardenTerrain?: Record<string, TerrainType>;
  /** FISHING: per-fish LIFETIME caught tally (fishId → count) from the pond minigame. CUMULATIVE and
   *  never decremented — it is the metric behind the `fishCaught:<id>` ladders, the `fishSpecies` count
   *  and the Field Guide's "found" flag. Selling writes `fishSold`, not this. */
  fishCaught?: Record<string, number>;
  /** FISHING: per-fish SOLD tally (fishId → count). The sellable bag is `fishCaught - fishSold`; an absent
   *  key means none sold. Saves written before this field existed had their sells subtracted from
   *  `fishCaught` directly, so they arrive with no `fishSold` and their bag reads exactly as it did. */
  fishSold?: Record<string, number>;
  /** INSECTS: per-species LIFETIME collected tally (butterflyId → count) from the garden. CUMULATIVE and
   *  never decremented — it is the metric behind the `insectCaught:<id>` ladders, the `insectSpecies` count
   *  and the Field Guide's "found" flag. Selling writes `insectSold`, not this. */
  butterfliesCaught?: Record<string, number>;
  /** INSECTS: per-species SOLD tally (insectId → count). The sellable bag is `butterfliesCaught - insectSold`;
   *  an absent key means none sold. Saves written before this field existed had their sells subtracted from
   *  `butterfliesCaught` directly, so they arrive with no `insectSold` and their bag reads exactly as it did. */
  insectSold?: Record<string, number>;
  /** Fish/insect ids whose Field-Guide DISCOVERY CONDITION the player has revealed (by catching a floating
   *  message-in-a-bottle / wind-blown note in the garden). Conditions are hidden by default until revealed. */
  revealedFish?: string[];
  revealedBugs?: string[];
  /** EQUIPPED FISHING GEAR: slot ("rod"/"reel"/"handle"/"hook"/"lure") → gear item id — and THE SOURCE OF
   *  TRUTH FOR THE RIG'S TIERS. A rig slot's id encodes its tier (`rod5` = rod at tier 5), so the upgrade
   *  track's whole progress is these four strings; `rigTiers()` in sim/fishingGear.ts decodes them and an
   *  absent slot reads as the free tier-1 starter. There is deliberately no separate persisted tier block —
   *  one would be a second copy of the same state to keep in sync. The LURE is the only real equip choice.
   *  Drives fishing stats (see sim/fishingGear.ts). */
  fishingGear?: Record<string, string>;
  /** OWNED fishing gear item ids the player has BOUGHT. LURES ONLY — the rig is a track, not an inventory,
   *  so a rig slot holds exactly one piece and has no owned-list beside it. */
  fishingGearOwned?: string[];
  /** GEAR UPGRADE rolls: per gear item id → its rarity + rolled stat lines (Gear Upgrade modal). A rig slot's
   *  roll is CARRIED FORWARD onto the new tier's id when the slot upgrades (see `migrateRig`). */
  gearRolls?: Record<string, GearRoll>;
  /** THE NEW-PLAYER GUIDE (src/sim/guide/engine.ts): finished steps, chapters and goals, by ID, plus the tips
   *  already shown. A save without it is migrated from the old `onboardingStep` below (`coerceGuide`). */
  guide?: GuideState;
  /** LEGACY — the old four-step tutorial's position (0–3, or 1000 = done). Read once, to migrate into `guide`;
   *  never written any more. */
  onboardingStep?: number;
  /** USAGE STATS — cumulative active-play time (ms) per scene + activity counters. OWNED by the /api/stats
   *  heartbeat (see sim/stats.ts), NOT by the normal save; carried through here only so it survives a
   *  full-save round-trip. Excluded from the save changeKey so incrementing it never forces a write. */
  usage?: UsageStats;
  /** ACHIEVEMENTS the player has unlocked (ids from sim/achievements.ts). Once earned, always kept. */
  unlockedAchievements?: string[];
  /** THE PLAYER'S AVATAR — the blossom they designed (sim/avatar.ts). Absent = never designed one; every view
   *  then shows a flower grown from their player id (`avatarFor`). The server reads it from here to put it on
   *  their public profile (server/profiles.js), shaped by its own copy of the coercion (server/avatar.js). */
  avatar?: AvatarSpec;
  /**
   * The earned achievements whose GARDEN CLUB reward half (decor, trophies, seeds, skins — sim/achievements.ts
   * `clubGrantOf` / `hasClubReward`) is still HELD: qualified, not yet collected, because the player is not a
   * member. Everything earned and NOT listed here counts as collected — App derives its collected set as
   * `unlockedAchievements − achClubHeld`, and the skins a player has come from that.
   *
   * STORED AS THE HELD SET, NOT THE COLLECTED ONE, for size: collected is almost all of `unlockedAchievements`
   * (34 KB on a full roster), and a second copy of it pushed a big-pond save over the 64 KiB keepalive cap.
   * Held is empty for every member and every pre-gate player, and only ever lists a non-member's prizes.
   *
   * ABSENT reads as empty, which is exactly right for a save from before the gate: its player was paid every
   * reward on unlock, so nothing they earned is held and nothing they hold is taken back.
   */
  achClubHeld?: string[];
  /**
   * POINT-TRACK MILESTONES the player has CLAIMED (ids from sim/pointTrack.ts). The track is claim-on-click:
   * reaching a milestone's point threshold only makes it CLAIMABLE, and nothing is stored until the player
   * clicks it — so this list is the whole of the track's persisted state. Reached-ness is recomputed from the
   * score every read and is deliberately not here.
   *
   * WHY IT IS IDS AND NOT A COUNT. A count would assume the track is only ever walked in order and never
   * re-tuned; an id survives both. Once claimed, always kept — same rule as `unlockedAchievements`, and for
   * the same reason: this list is the receipt for a payout that has already been made, so dropping an entry
   * pays the player twice.
   */
  claimedMilestones?: string[];
  /** SHOP PLANTS WHOSE SEEDS HAVE BEEN PAID OUT (sim/premiumSeeds.ts): buying a plant in the Premium Shop puts
   *  PREMIUM_SEED_GRANT of its seeds in the bag, once. The receipt, like `claimedMilestones` — dropping an id
   *  pays that plant's seeds again on the next load. */
  premiumSeedsGranted?: string[];
  /** KICKSTARTER DEMO: one-time reward XP the level cap would have cut (sim/kickstarter.ts `heldByCap`), held
   *  here and paid the first time the game loads with the mode off. Absent = nothing held. */
  ksHeldXp?: HeldXp;
  /** PER-POT cosmetic skins: pot slotKey ("rack-tier-col") → skin id. Only non-default pots are stored;
   *  a pot with no entry uses the default terracotta. (It replaced a single global `selectedPotSkin`, which
   *  was never migrated — pot keys are not known at load — and is no longer read or written anywhere.) */
  potSkins?: Record<string, string>;
  /** The selected cosmetic GREENHOUSE SHELL skin id (sim/houseSkins.ts) — glazing, frame and outer walls.
   *  Absent = the default "glasshouse" (the original art). An id from a build that no longer ships the skin
   *  resolves to the default too, so a save never fails to open on a retired cosmetic. */
  selectedBuildingSkin?: string;
  /** The selected cosmetic greenhouse FLOOR skin id (sim/houseSkins.ts). Absent = the default "stone".
   *  Same unknown-id rule as `selectedBuildingSkin`. */
  selectedFloorSkin?: string;
  /** Cosmetic greenhouse RACK (shelf) skin id (sim/shelfSkins.ts). Same unknown-id rule as the others. */
  selectedShelfSkin?: string;
  /** THE REWARD CATCH-UP has run (1): achievements held before decor rewards existed have paid their decor
   *  and trophies once. Absent → it runs on the next load. */
  rewardCatchup?: number;
  /** Total plants harvested (greenhouse + garden), an achievement metric with no prior counter. */
  harvestCount?: number;
  /**
   * Per-species harvest COUNTS (species id → how many). Drives both the "distinct species" achievements
   * (a key exists ⇒ at least one harvested) and the per-plant harvest ladder.
   *
   * MIGRATED FROM BOOLEANS: this was `Record<string, true>` and only recorded WHETHER a species had been
   * harvested. Old saves are read as a count of 1 — the honest floor, since "true" only ever proved one.
   * A number is truthy, so every existing `if (harvestedSpecies[id])` check keeps working unchanged.
   */
  harvestedSpecies?: Record<string, number>;
}

/** Player usage counters. `*Ms` fields are ACTIVE-play milliseconds (accrual pauses when the tab is
 *  hidden/idle). `firstSeenMs`/`lastSeenMs` are epoch timestamps, not durations. */
export interface UsageStats {
  greenhouseMs: number;
  gardenMs: number;
  fishingMs: number;
  sessions: number;
  firstSeenMs: number;
  lastSeenMs: number;
}

/** Coins a brand-new player starts with (tuned for the intro tutorial — enough for one cheap decor + a
 *  little slack, not a fortune). Single source of truth for the starting balance. */
/** A save's sprinkler settings, sanitised: the run time clamped to 0..120 minutes, anything else dropped.
 *  Hand-editable server JSON must never reach the watering logic as a string or a negative. (Reads the
 *  short-lived `maxRunMin` name too — a save from the first console build carried the run time under it —
 *  and drops that build's `keepDamp`, which was removed as too easy.) */
export function coerceSprinklerSettings(raw: unknown): Record<string, { runMin: number }> {
  const out: Record<string, { runMin: number }> = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!v || typeof v !== "object") continue;
    const o = v as Record<string, unknown>;
    const n = typeof o.runMin === "number" ? o.runMin : o.maxRunMin;
    out[k] = { runMin: typeof n === "number" && Number.isFinite(n) ? Math.max(0, Math.min(120, n)) : 0 };
  }
  return out;
}

export const STARTING_COINS = 200;

export const SAVE_VERSION = 2;   // v2: added compost soilStock + per-pot/patch soil (all additive)

/** A fresh game with no progress. The garden starts with a default pond (player-water
 *  cells, so it can be extended or erased with the water tool). */
export function freshSave(): SaveData {
  return {
    // New players START with a single shelf (rackCount 1) and buy more to the right (up to MAX_RACKS).
    // Existing saves keep whatever rackCount they had (coerce preserves it), so this only affects new games.
    // `guide: freshGuide()` = a brand-new player → the guide's First Harvest runs (and resetSave() re-triggers
    // it, since it writes freshSave()).
    v: SAVE_VERSION, coins: STARTING_COINS, petals: 0, checkin: freshCheckin(), subscription: { ...NO_SUBSCRIPTION },
    xp: 0, xpCurve: XP_CURVE_VERSION, seeds: {}, stock: {}, rackCount: 1, plantings: {}, equipment: {},
    gardenWater: startingWaterCells(), guide: freshGuide(),
    // the garden starts with the OAK tree, now a real (movable/sellable) decor item. `decorOwned.oakTree: 1`
    // is NOT decoration of the placement — it is the ledger entry the placement spends. `availableDecor` is
    // `owned - placed`, so without the 1 here a new player's starter oak is a placement with no owner: buy a
    // second oak for 7,000 coins and the bag still reads 0 (owned 1 / placed 1) and the purchase is unplaceable.
    decorOwned: { oakTree: 1 }, decor: [{ ...STARTER_OAK }], starterOak: true,
    // the rig starts on its free tier-1 pieces — the floor of the upgrade track, not an empty rig. Written as
    // equipped ids because that IS the tier representation (see `fishingGear` above); `freshSave()` is handed
    // straight to the UI without passing through `coerceSave`, so nothing else would fill these in.
    fishingGear: rigEquipped(STARTER_RIG_TIERS),
  };
}

/**
 * Swappable persistence backend. The SYNC shape (localStorage) loads instantly at
 * startup; the ASYNC shape (a future Railway API) returns promises. App code today
 * uses the sync `localStore`; Phase 2 adds an async store + an await on boot.
 */
/** Why a save is being written. `"unload"` means the tab is going away (pagehide / visibilitychange →
 *  hidden) and the request must outlive the document — the ONLY case that may ask for `keepalive`, which
 *  the browser caps at 64 KiB of body. Everything else is a normal in-page write with no such cap. */
export type SaveReason = "normal" | "unload";

export interface SyncSaveStore {
  load(): SaveData | null;
  save(data: SaveData, reason?: SaveReason): void;
}
export interface AsyncSaveStore {
  load(): Promise<SaveData | null>;
  save(data: SaveData, reason?: SaveReason): Promise<void>;
}
export type SaveStore = SyncSaveStore | AsyncSaveStore;

/**
 * THE LOCAL MIRROR IS KEYED BY PLAYER. One browser, several accounts — a shared family laptop, a
 * demo machine, anyone who signs out and signs in as someone else — and the mirror is the one piece
 * of save state that is not already namespaced by the server's row.
 *
 * THE BUG THIS FIXES: the mirror lived at a single un-namespaced key, so player B signing up on A's
 * browser hit `GET /api/save → 404` ("no cloud save yet"), found A's mirror sitting at that key, and
 * PUT A'S WHOLE GARDEN as B's brand-new account save. A's progress became B's, and the 404 import —
 * which exists to rescue pre-cloud local progress — was the thing that did it.
 *
 * So: `shelf-garden.save.<ownerId>`. The owner is the Clerk user id when signed in (pushed in by
 * ui/AuthGate via `setSaveOwner`), else the anonymous per-browser id — the same identity the save
 * request itself is keyed by, so the mirror can never disagree with the row it mirrors.
 */
const LEGACY_KEY = "shelf-garden.save.v1";
const KEY_PREFIX = "shelf-garden.save.";

/**
 * WHOSE mirror this tab reads and writes. `null` = nobody has claimed it, so fall back to the
 * anonymous browser id (a no-auth build, or the window before Clerk resolves).
 */
let _saveOwner: string | null = null;

/**
 * Claim the local mirror for a player id (the Clerk user id). Called by the auth layer alongside
 * `setAuthTokenGetter`, and with `null` on sign-out so the next reader falls back to the anonymous id
 * rather than keeping the departed account's key.
 *
 * Changing owner does NOT move or copy any stored save: each id already has its own key, and the point
 * is that signing in as someone else cannot see the previous account's garden.
 */
export function setSaveOwner(id: string | null): void {
  _saveOwner = id && id.length ? id : null;
}

/** The claimed owner id, or null (anonymous) — names the one-active-tab lock (sim/playLock.ts). */
export function saveOwner(): string | null { return _saveOwner && sandboxOwner(_saveOwner); }

/** The mirror key for the current owner. */
function mirrorKey(): string {
  return KEY_PREFIX + sandboxOwner(_saveOwner ?? getPlayerId());
}

/**
 * ONE-TIME ADOPTION OF THE PRE-NAMESPACE MIRROR.
 *
 * Every existing player has a save at the old un-namespaced key. The first owner to look for a mirror
 * and find none takes that one over (copying it to their key) and DELETES the legacy key, so it can be
 * adopted exactly once — the anonymous → signed-in migration this repo's players actually walk through,
 * without leaving a copy behind for the next account on the browser to inherit.
 *
 * Returns the adopted raw JSON, or null when there is nothing legacy left to adopt. The delete happens
 * even if the copy fails (quota): a legacy blob that can't be re-homed is still not something a second
 * account may pick up.
 */
function adoptLegacyMirror(key: string): string | null {
  try {
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy === null) return null;
    try { localStorage.setItem(key, legacy); } catch { /* quota — the delete below still stands */ }
    localStorage.removeItem(LEGACY_KEY);
    return legacy;
  } catch {
    return null;   // storage blocked — nothing to migrate
  }
}

/**
 * Migrate/validate a parsed blob into a current SaveData, filling any missing
 * fields from a fresh save. Tolerant: a partial or older save still loads cleanly.
 *
 * EXPORTED for garden VISITING (ui/GardenVisit.tsx), which receives a deliberately stripped world blob
 * from the server and needs the rest filled with defaults — the visitor sees zero coins and an empty
 * inventory, which is correct, since none of it is theirs and none of it is displayed.
 */
/** Owned equipment, keeping only ids that still exist. The solar panels (`solarMini`) and home battery
 *  (`batterySmall`) were removed from the game; a save that owned them simply loses the entries — the
 *  coins spent on them are gone, not refunded. */
function coerceEquipment(v: unknown): Record<string, number> {
  if (!v || typeof v !== "object" || Array.isArray(v)) return {};
  const out: Record<string, number> = {};
  for (const [id, n] of Object.entries(v as Record<string, unknown>)) {
    if ((EQUIPMENT_IDS as string[]).includes(id) && typeof n === "number") out[id] = n;
  }
  return out;
}

/** FARMING WAS REMOVED (the hoe → plant crop → harvest loop in the iso garden). A save written while it
 *  existed may still carry a `soil` array of tilled patches and seeds/stock of the crop species it registered.
 *  `coerce` builds the save field by field, so `soil` is dropped simply by not being read — that omission IS
 *  the migration, and those cells come back as plain grass. The crop ids are listed here only so their
 *  leftover seed/stock counts are dropped too: with the catalogue gone, `flower("carrot")` would resolve to the
 *  first flower and the bag would show a phantom stack. Keep both for as long as old saves can load. */
const REMOVED_CROP_IDS: ReadonlySet<string> = new Set([
  "carrot", "lettuce", "radish", "wheat", "tomato", "strawberry", "pepper", "corn", "pumpkin",
]);
function coerceCounts(v: unknown): Record<string, number> {
  if (!v || typeof v !== "object") return {};
  const out = { ...v } as Record<string, number>;
  for (const id of REMOVED_CROP_IDS) delete out[id];
  return out;
}

export function coerce(raw: unknown): SaveData | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Partial<SaveData>;
  const base = freshSave();
  // XP-CURVE MIGRATION (sim/xpCurve.ts) — FIRST, before anything reads a level: a curve-1 save's totals are
  // re-expressed on the current curves so every track keeps its level and its progress into it. `migrateRig`
  // below derives a FISHING LEVEL from fishXp to seed its stat roll, so it must see the converted total.
  const xpc = migrateXpCurve({ xp: o.xp, fishXp: o.fishXp, bugXp: o.bugXp, xpCurve: o.xpCurve });
  return {
    v: SAVE_VERSION,
    coins: typeof o.coins === "number" ? o.coins : base.coins,
    petals: typeof o.petals === "number" && o.petals >= 0 ? Math.round(o.petals) : 0,
    petalsDebited: typeof o.petalsDebited === "number" && Number.isFinite(o.petalsDebited) && o.petalsDebited > 0 ? Math.floor(o.petalsDebited) : 0,
    coinsDebited: typeof o.coinsDebited === "number" && Number.isFinite(o.coinsDebited) && o.coinsDebited > 0 ? Math.floor(o.coinsDebited) : 0,
    petalsCredited: typeof o.petalsCredited === "number" && Number.isFinite(o.petalsCredited) && o.petalsCredited > 0 ? Math.floor(o.petalsCredited) : 0,
    grantsCredited: coerceGrantLedger(o.grantsCredited),
    checkin: coerceCheckin(o.checkin),
    subscription: coerceSubscription(o.subscription),
    xpCurve: XP_CURVE_VERSION,
    xp: typeof xpc.xp === "number" ? xpc.xp : base.xp,
    fishXp: typeof xpc.fishXp === "number" ? xpc.fishXp : 0,
    bugXp: typeof xpc.bugXp === "number" ? xpc.bugXp : 0,
    bugTalents: coerceBugTalents(o.bugTalents),
    exoticLicense: o.exoticLicense === true,
    robot: o.robot === true,
    robotAnnounced: o.robotAnnounced === true,
    robotOrders: coerceOrders(o.robotOrders),
    robotShiftReq: typeof o.robotShiftReq === "number" && Number.isFinite(o.robotShiftReq) && o.robotShiftReq > 0 ? o.robotShiftReq : 0,
    robotShiftStart: typeof o.robotShiftStart === "number" && Number.isFinite(o.robotShiftStart) && o.robotShiftStart > 0 ? o.robotShiftStart : 0,
    robotLoadout: o.robotLoadout && typeof o.robotLoadout === "object" && !Array.isArray(o.robotLoadout)
      ? Object.fromEntries(Object.entries(o.robotLoadout).filter(([, v]) => typeof v === "string")) as Record<string, string>
      : undefined,
    seeds: coerceCounts(o.seeds),
    stock: coerceCounts(o.stock),
    compostBin: coerceCompostBin(o.compostBin),
    compostBarrel: coerceBarrel(o.compostBarrel),
    potCompost: coercePotCompost(o.potCompost),
    composter: coerceComposter(o.composter, gameNow(), coerceCompost) ?? undefined,
    potTimers: Array.isArray(o.potTimers) ? o.potTimers.filter((k): k is string => typeof k === "string") : [],
    timersOff: Array.isArray(o.timersOff) ? o.timersOff.filter((k): k is string => typeof k === "string") : [],
    potMoisture: coercePotMoisture(o.potMoisture),
    rackCount: typeof o.rackCount === "number" ? o.rackCount : base.rackCount,
    plantings: o.plantings && typeof o.plantings === "object" ? coercePlantings(o.plantings) : {},
    equipment: coerceEquipment(o.equipment),
    compostTier: typeof o.compostTier === "number" ? o.compostTier : 0,
    gridPower: o.gridPower === true,
    lastPowerAccrualMs: typeof o.lastPowerAccrualMs === "number" ? o.lastPowerAccrualMs : undefined,
    powerBill: typeof o.powerBill === "number" && o.powerBill >= 0 ? o.powerBill : 0,
    powerCredit: typeof o.powerCredit === "number" && o.powerCredit >= 0 ? o.powerCredit : 0,
    autopay: o.autopay === true,
    growLights: Array.isArray(o.growLights) ? o.growLights.filter((k): k is string => typeof k === "string") : [],
    growLightTiers: o.growLightTiers && typeof o.growLightTiers === "object" && !Array.isArray(o.growLightTiers) ? { ...o.growLightTiers } as Record<string, number> : {},
    growLightColors: o.growLightColors && typeof o.growLightColors === "object" && !Array.isArray(o.growLightColors) ? { ...o.growLightColors } as Record<string, string> : {},
    lightRules: o.lightRules && typeof o.lightRules === "object" && !Array.isArray(o.lightRules) ? stripRemovedConditions(o.lightRules) : {},
    lightManual: o.lightManual && typeof o.lightManual === "object" && !Array.isArray(o.lightManual) ? { ...o.lightManual } as Record<string, boolean> : {},
    sprinklers: Array.isArray(o.sprinklers) ? o.sprinklers.filter((k): k is string => typeof k === "string") : [],
    sprinklerRules: o.sprinklerRules && typeof o.sprinklerRules === "object" && !Array.isArray(o.sprinklerRules) ? stripRemovedConditions(o.sprinklerRules) : {},
    sprinklerSettings: coerceSprinklerSettings(o.sprinklerSettings),
    plot: coercePlot(o.plot),
    // Plants saved on a decor that has since stopped being a host (the rethemed `wallTrough`) go back to the
    // bag rather than standing on the grass inside it — see liftFromRetiredHosts. The starter-oak injection
    // below cannot change the answer (the oak is not a host), so the raw decor list is enough here.
    garden: liftFromRetiredHosts(coercePlacements(o.garden), coerceDecorPlacements(o.decor)),
    // ONE-TIME MIGRATION: the starting oak used to be a hardcoded scene tree. For any save from before that
    // change (no `starterOak` flag), inject the oak decor at TREE_BASE so the player keeps their tree, AND
    // credit `decorOwned.oakTree` for it — the placement has to be backed by a ledger entry or the bag reads
    // `owned - placed` = 0 and a bought oak can never be placed (same reason freshSave seeds it).
    //
    // `starterOak: true` is what makes this ONE-time: it goes out in App's snapshot() and the server keeps it,
    // so a player who picks the oak up or sells it does not get it shoved back at TREE_BASE on the next load.
    // (It used to be set here and nowhere else, which meant it was never persisted and the migration re-ran
    // on every single load.) `decorOwned` is produced INSIDE this block rather than on its own line above,
    // because the credit depends on the placements the block may have just injected.
    ...(() => {
      // Both coercers DROP ids that are no longer in DECOR. A decor retirement's refund/swap migration goes
      // HERE, reading the raw o.decor / o.decorOwned before they do (see the note above sim/decor.ts DECOR).
      const decor = coerceDecorPlacements(o.decor);
      const decorOwned = coerceDecorOwned(o.decorOwned);
      if (!o.starterOak) {
        if (!decor.some((d) => d.id === STARTER_OAK.id)) decor.push({ ...STARTER_OAK });
        // Credit the oak only up to what is placed: a pre-migration save that already bought oaks of its own
        // keeps those, and the starter adds exactly the one it occupies.
        const placed = placedDecorCount(decor, STARTER_OAK.decor);
        if ((decorOwned[STARTER_OAK.decor] ?? 0) < placed) decorOwned[STARTER_OAK.decor] = placed;
      }
      return { decorOwned, decor, starterOak: true };
    })(),
    fences: coerceFences(o.fences),
    fenceStyles: coerceFenceStyles(o.fenceStyles, coerceFences(o.fences)),
    gardenWater: coerceWater(o.gardenWater),
    gardenTerrain: coerceTerrain(o.gardenTerrain),
    // No `soil`: farming was removed, and leaving an old save's tilled patches unread is what strips them
    // (see REMOVED_CROP_IDS above).
    fishCaught: coerceFishCaught(o.fishCaught),
    // NO MIGRATION, deliberately: a pre-`fishSold` save already had its sells taken out of `fishCaught`,
    // so an empty sold map leaves that player's bag exactly where they left it. Back-filling a guess here
    // would either hand back fish they had spent or take away fish they still hold.
    fishSold: coerceFishSold(o.fishSold),
    butterfliesCaught: coerceButterfliesCaught(o.butterfliesCaught),
    // NO MIGRATION, for the same reason as `fishSold`: an old save's sells already came out of the caught map.
    insectSold: coerceInsectSold(o.insectSold),
    revealedFish: coerceRevealedFish(o.revealedFish),
    revealedBugs: coerceRevealedBugs(o.revealedBugs),
    // FISHING GEAR — TWO migrations, in this order and both still needed:
    //   1. the "line" rig slot renamed to "handle" (slot key + item ids line1..8 → handle1..8), and
    //   2. the rig shop → the UPGRADE TRACK (`migrateRig` above), which reads the already-renamed ids.
    // A pre-rename save therefore passes through both; a post-track save passes through unchanged.
    ...(() => {
      const equipped = o.fishingGear && typeof o.fishingGear === "object" && !Array.isArray(o.fishingGear)
        ? Object.fromEntries(Object.entries(o.fishingGear)
            .filter(([, v]) => typeof v === "string")
            .map(([k, v]) => [k === "line" ? "handle" : k, migrateGearId(v as string)])) as Record<string, string>
        : {};
      const owned = Array.isArray(o.fishingGearOwned)
        ? o.fishingGearOwned.filter((k): k is string => typeof k === "string").map(migrateGearId)
        : [];
      // GEAR ROLLS — migrate any line* keys to handle* as well (they key by gear item id).
      const rolls: Record<string, GearRoll> = {};
      for (const [k, v] of Object.entries(coerceGearRolls(o.gearRolls))) rolls[migrateGearId(k)] = v;
      const fishXp = typeof xpc.fishXp === "number" ? xpc.fishXp : 0;   // on the CURRENT curve (xpc, above)
      return migrateRig(equipped, owned, rolls, fishXp);
    })(),
    // THE GUIDE — cleaned, or migrated from the old tutorial's `onboardingStep` (engine.ts `coerceGuide`): a save
    // without either is a player from before any tutorial, and starts with First Harvest behind them.
    guide: coerceGuide(o.guide, o.onboardingStep),
    // USAGE STATS — passed through if present (the server owns it via the heartbeat). Never reset here.
    usage: coerceUsage(o.usage),
    // WHEN THIS SNAPSHOT WAS TAKEN — the load-time tie-breaker (see `newerSave`). A missing / malformed stamp
    // reads as 0 rather than "now": an unstamped copy must LOSE to a stamped one, not beat it.
    savedAt: typeof o.savedAt === "number" && Number.isFinite(o.savedAt) ? o.savedAt : 0,
    // ACHIEVEMENTS + cosmetics.
    unlockedAchievements: Array.isArray(o.unlockedAchievements) ? o.unlockedAchievements.filter((k): k is string => typeof k === "string") : [],
    avatar: coerceAvatar(o.avatar) ?? undefined,
    achClubHeld: Array.isArray(o.achClubHeld) ? o.achClubHeld.filter((k): k is string => typeof k === "string") : [],
    // POINT-TRACK CLAIMS — filtered to strings, exactly as the unlocked-achievement ids above, so a
    // malformed save degrades to "nothing claimed yet" instead of putting a non-string into the claimed Set
    // and crashing the track on load. Unknown ids are KEPT: an id whose milestone was retired or renamed in
    // the dev menu scores nothing (`milestone()` returns undefined), but dropping it here would let a
    // restored milestone pay out a second time.
    claimedMilestones: Array.isArray(o.claimedMilestones) ? o.claimedMilestones.filter((k): k is string => typeof k === "string") : [],
    premiumSeedsGranted: Array.isArray(o.premiumSeedsGranted) ? o.premiumSeedsGranted.filter((k): k is string => typeof k === "string") : [],
    ksHeldXp: coerceHeldXp(o.ksHeldXp),
    selectedBuildingSkin: typeof o.selectedBuildingSkin === "string" ? o.selectedBuildingSkin : undefined,
    selectedFloorSkin: typeof o.selectedFloorSkin === "string" ? o.selectedFloorSkin : undefined,
    selectedShelfSkin: typeof o.selectedShelfSkin === "string" ? o.selectedShelfSkin : undefined,
    rewardCatchup: typeof o.rewardCatchup === "number" ? o.rewardCatchup : undefined,
    potSkins: o.potSkins && typeof o.potSkins === "object" && !Array.isArray(o.potSkins)
      ? Object.fromEntries(Object.entries(o.potSkins).filter(([, v]) => typeof v === "string")) as Record<string, string>
      : {},
    harvestCount: typeof o.harvestCount === "number" && o.harvestCount >= 0 ? o.harvestCount : 0,
    // Accepts BOTH shapes: the legacy boolean map (true → 1) and the current count map. Values are clamped
    // to a non-negative integer so a corrupt save cannot inject a NaN into an achievement threshold test.
    harvestedSpecies: o.harvestedSpecies && typeof o.harvestedSpecies === "object" && !Array.isArray(o.harvestedSpecies)
      ? Object.fromEntries(
          Object.entries(o.harvestedSpecies)
            .map(([k, raw]) => {
              // `raw` is untrusted JSON: the declared type says number, but a pre-migration save holds `true`.
              const v = raw as unknown;
              const n = v === true ? 1 : (typeof v === "number" && Number.isFinite(v) ? Math.max(0, Math.trunc(v)) : 0);
              return [k, n] as const;
            })
            .filter(([, n]) => n > 0),
        ) as Record<string, number>
      : {},
  };
}

/** Coerce the usage-stats block (all counters non-negative numbers). Undefined stays undefined so a fresh
 *  save carries no usage until the first heartbeat writes it server-side. */
export function coerceUsage(raw: unknown): UsageStats | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Partial<UsageStats>;
  const n0 = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : 0);
  return {
    greenhouseMs: n0(o.greenhouseMs),
    gardenMs: n0(o.gardenMs),
    fishingMs: n0(o.fishingMs),
    sessions: n0(o.sessions),
    firstSeenMs: n0(o.firstSeenMs),
    lastSeenMs: n0(o.lastSeenMs),
  };
}

const VALID_EFFECTS = new Set(["double", "xp", "value", "quickBloom", "pollinate", "retain"]);
/** Validate a StoredCompost batch ({ effects[] }), or null if malformed. Quality was removed — a legacy
 *  batch that only had a `quality` and no valid effects simply becomes an empty (harmless) batch. Old
 *  3-essence soil ({ sand, loam, peat, potency }) is dropped (no `effects` array). */
function coerceCompost(raw: unknown): StoredCompost | null {
  const s = raw as Record<string, unknown> | null | undefined;
  if (!s || typeof s !== "object") return null;
  if (!Array.isArray(s.effects)) return null;   // not the batch shape → drop
  const effects: StoredEffect[] = [];
  for (const e of s.effects as unknown[]) {
    const ee = e as Record<string, unknown> | null;
    if (ee && typeof ee.type === "string" && VALID_EFFECTS.has(ee.type) && typeof ee.amount === "number") {
      const amount = ee.amount as number;
      const min = typeof ee.min === "number" ? ee.min : amount;
      const max = typeof ee.max === "number" ? ee.max : amount;
      effects.push({ type: ee.type as CompostEffectId, amount, min, max });
    }
  }
  const rank = typeof s.rank === "number" && s.rank >= 1 && s.rank <= 5 ? Math.round(s.rank) : undefined;
  return rank ? { effects, rank } : { effects };
}

/** Validate the plantings map, preserving the optional per-plant `compost` batch if present. The record is
 *  rebuilt field by field, so anything not copied here is DROPPED on load — including the legacy per-planting
 *  `genome` (flower genetics, removed; every live planting was wild-type), which old saves may still carry. */
function coercePlantings(raw: Record<string, SavedPlanting>): Record<string, SavedPlanting> {
  const out: Record<string, SavedPlanting> = {};
  for (const [k, p] of Object.entries(raw)) {
    if (!p || typeof p !== "object" || typeof p.species !== "string" || typeof p.plantedAt !== "number") continue;
    const rec: SavedPlanting = { species: p.species, plantedAt: p.plantedAt };
    const compost = coerceCompost((p as SavedPlanting).compost);
    if (compost) rec.compost = compost;
    if (typeof p.grown === "number") rec.grown = p.grown;
    if (typeof p.lastAt === "number") rec.lastAt = p.lastAt;
    // LIT-BY HISTORY — seconds spent under each light COLOUR, and the keys must be real colour ids.
    // An unknown key (a hand-edited save, or a colour retired in a later build) is DROPPED here rather
    // than carried: the dominant-colour pick feeds straight into `lightColor`, and a key that isn't in
    // LIGHT_COLORS would otherwise decide the harvest effect off a fallback colour the plant never
    // actually grew under. Dropping it on load makes the SECOND-most-lit real colour win instead.
    if (p.litBy && typeof p.litBy === "object" && !Array.isArray(p.litBy)) {
      const lit: Record<string, number> = {};
      for (const [c, v] of Object.entries(p.litBy)) if (typeof v === "number" && v > 0 && c in LIGHT_COLORS) lit[c] = v;
      if (Object.keys(lit).length) rec.litBy = lit;
    }
    if (typeof p.litTier === "number") rec.litTier = p.litTier;
    if (typeof p.giftedGrown === "number" && Number.isFinite(p.giftedGrown) && p.giftedGrown > 0) rec.giftedGrown = p.giftedGrown;
    out[k] = rec;
  }
  return out;
}

/** Validate the compost bin (an array of batches). */
function coerceCompostBin(raw: unknown): StoredCompost[] {
  if (!Array.isArray(raw)) return [];
  const out: StoredCompost[] = [];
  for (const v of raw) { const b = coerceCompost(v); if (b) out.push(b); }
  return out;
}
/** Validate the composter BARREL. Undefined/empty → no barrel. Keeps only known plant-id strings, a
 *  numeric sealedAt, and a points map over valid effects. */
function coerceBarrel(raw: unknown): StoredBarrel | undefined {
  const s = raw as Record<string, unknown> | null | undefined;
  if (!s || typeof s !== "object" || Array.isArray(s)) return undefined;
  const plants = Array.isArray(s.plants) ? s.plants.filter((p): p is FlowerId => typeof p === "string") : [];
  const b: StoredBarrel = { plants };
  if (typeof s.sealedAt === "number") b.sealedAt = s.sealedAt;
  if (typeof s.tier === "number" && Number.isFinite(s.tier)) b.tier = Math.max(0, Math.min(4, Math.round(s.tier)));
  if (s.points && typeof s.points === "object" && !Array.isArray(s.points)) {
    const pts: Partial<Record<CompostEffectId, number>> = {};
    for (const [k, v] of Object.entries(s.points as Record<string, unknown>)) {
      if (VALID_EFFECTS.has(k) && typeof v === "number" && v > 0) pts[k as CompostEffectId] = Math.round(v);
    }
    if (Object.keys(pts).length) b.points = pts;
  }
  // an empty, unsealed barrel is equivalent to no barrel.
  if (!b.plants.length && b.sealedAt === undefined) return undefined;
  return b;
}

/** Validate the per-pot loaded compost map. */
function coercePotCompost(raw: unknown): Record<string, StoredCompost> {
  const out: Record<string, StoredCompost> = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const b = coerceCompost(v); if (b) out[k] = b;
  }
  return out;
}

/** Validate the per-pot moisture checkpoint map. `drySince` survives only as a finite number — anything
 *  else is dropped, and the sim re-derives the dry spell from the level (sim/moisture drySinceAt). */
function coercePotMoisture(raw: unknown): Record<string, { level: number; at: number; drySince?: number }> {
  const out: Record<string, { level: number; at: number; drySince?: number }> = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const m = v as Record<string, unknown> | null;
    if (m && typeof m === "object" && typeof m.level === "number" && typeof m.at === "number") {
      const cp: { level: number; at: number; drySince?: number } = { level: Math.max(0, Math.min(1, m.level)), at: m.at };
      if (typeof m.drySince === "number" && Number.isFinite(m.drySince)) cp.drySince = m.drySince;
      out[k] = cp;
    }
  }
  return out;
}

/**
 * THE SAVE AS IT IS WRITTEN — local mirror, cloud PUT, kept-snapshot. Identical to `data` except the two
 * painted sub-grids, which go as row runs (`packCells` / `packTerrain`) instead of one JSON key per
 * sub-cell: a max-size garden painted edge to edge is ~3.5 KB this way and was ~590 KB the other (see the
 * note at the WATER section of sim/garden.ts). Everything that READS a save goes through `coerce`, which
 * accepts both forms, so no reader needs to know. Not a `SaveData` — only ever handed to JSON.stringify.
 */
export function packForWire(data: SaveData): Record<string, unknown> {
  return {
    ...data,
    ...(data.gardenWater ? { gardenWater: packCells(data.gardenWater) } : {}),
    ...(data.gardenTerrain ? { gardenTerrain: packTerrain(data.gardenTerrain) } : {}),
  };
}

/** The default localStorage-backed store (synchronous, offline). Reads/writes the CURRENT OWNER's
 *  mirror key only — see `mirrorKey` / `setSaveOwner` above for why it is namespaced. */
export const localStore: SyncSaveStore = {
  load() {
    try {
      const key = mirrorKey();
      // No mirror under this owner yet ⇒ this may be the anonymous → signed-in hand-off, so take over the
      // pre-namespace save if one is still there. Exactly once: the adoption deletes the legacy key.
      // (a tutorial test save starts empty — it must never adopt, or retire, the real game's legacy copy)
      const s = localStorage.getItem(key) ?? (sandboxActive() ? null : adoptLegacyMirror(key));
      if (!s) return null;
      return coerce(JSON.parse(s));
    } catch {
      return null;   // corrupt/unavailable → start fresh
    }
  },
  save(data) {
    try {
      // Writing the owner's mirror RETIRES the legacy key too: this owner now has a save of their own, so
      // the un-namespaced copy is dead weight that the next account to sign in here must not inherit.
      localStorage.setItem(mirrorKey(), JSON.stringify(packForWire(data)));
      if (!sandboxActive()) try { localStorage.removeItem(LEGACY_KEY); } catch { /* non-fatal */ }
    } catch {
      /* storage full / disabled — non-fatal */
    }
  },
};

// ---- Cloud (Railway) store --------------------------------------------------

/**
 * AUTH: when Clerk is active, the app injects a token getter here. The cloud store
 * then sends `Authorization: Bearer <token>` so the SERVER derives the player id
 * from the verified Clerk session (can't be spoofed). When no getter is set
 * (offline/dev/no-auth build), it falls back to the anonymous x-player-id header.
 */
let _getToken: (() => Promise<string | null>) | null = null;
export function setAuthTokenGetter(fn: (() => Promise<string | null>) | null): void {
  _getToken = fn;
}
/** True once a Clerk token getter is wired in (i.e. auth-gated build). */
export function authActive(): boolean { return _getToken != null; }

/** The current Clerk JWT (or null if no auth / not signed in). For places that can't send a Bearer HEADER
 *  — e.g. a WebSocket upgrade, where the token must ride in the URL query. */
export async function getAuthToken(): Promise<string | null> {
  if (!_getToken) return null;
  try { return await _getToken(); } catch { return null; }
}

/** Build request headers: Bearer token when authed, else the anon player-id.
 *  Exported so the admin API client can reuse the same authenticated headers. */
export async function authHeaders(base: Record<string, string> = {}): Promise<Record<string, string>> {
  // the tutorial test save (sim/sandbox.ts): the server reads and writes the throwaway row, and refuses other writes
  if (sandboxActive()) base = { ...base, [SANDBOX_HEADER]: SANDBOX_KIND };
  if (_getToken) {
    try {
      const tok = await _getToken();
      if (tok) return { ...base, authorization: `Bearer ${tok}` };
    } catch { /* fall through to anon */ }
  }
  return { ...base, "x-player-id": getPlayerId() };
}

/** Stable anonymous PLAYER ID for this browser. Generated once and kept in
 *  localStorage, sent to the server so the save follows this browser. (Used only
 *  in the no-auth fallback; authed builds key saves by the Clerk user id.) */
const PLAYER_ID_KEY = "shelf-garden.playerId";
export function getPlayerId(): string {
  try {
    let id = localStorage.getItem(PLAYER_ID_KEY);
    if (!id) {
      id = (crypto.randomUUID?.() ?? `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`);
      localStorage.setItem(PLAYER_ID_KEY, id);
    }
    return id;
  } catch {
    // storage blocked → ephemeral per-session id
    return `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
  }
}

// ---- Sync status (for the UI indicator) ------------------------------------

/** Cloud sync state the UI can surface. */
export type SyncStatus = "idle" | "saving" | "saved" | "offline";

let _status: SyncStatus = "idle";
const statusListeners = new Set<(s: SyncStatus) => void>();

/** Current cloud sync status. */
export function syncStatus(): SyncStatus { return _status; }
/** Subscribe to sync-status changes; returns an unsubscribe fn. */
export function onSyncStatus(fn: (s: SyncStatus) => void): () => void {
  statusListeners.add(fn);
  return () => statusListeners.delete(fn);
}
function setStatus(s: SyncStatus): void {
  if (s === _status) return;
  _status = s;
  statusListeners.forEach((fn) => fn(s));
}

// ---- Lost save races (for the "another device saved more recently" notice) --------------------

/**
 * A snapshot this device LOST a save race with (`apiStore.save`'s 409 branch), kept so the player can take
 * it back. `dismissed` is the "one notice per conflict" flag: closing the notice sets it, the copy itself
 * stays until it expires, and a NEW conflict replaces the whole record — so each race shows exactly once.
 */
export interface LostSave { keptAt: number; dismissed: boolean; data: SaveData }

/** How long a lost snapshot is kept on this device. Device wall clock — a local housekeeping TTL, not game time. */
export const LOST_SAVE_KEEP_MS = 24 * 60 * 60 * 1000;
/** Its own prefix, not `KEY_PREFIX`: a key under the mirror prefix could collide with an owner id. */
const LOST_PREFIX = "shelf-garden.lost-save.";
function lostKey(): string { return LOST_PREFIX + sandboxOwner(_saveOwner ?? getPlayerId()); }
const lostListeners = new Set<(l: LostSave | null) => void>();

/** The current owner's kept losing snapshot, or null. Reading it also drops one that has expired. */
export function lostSave(): LostSave | null {
  try {
    const raw = localStorage.getItem(lostKey());
    if (!raw) return null;
    const rec = JSON.parse(raw) as { keptAt?: unknown; dismissed?: unknown; data?: unknown };
    const keptAt = typeof rec.keptAt === "number" ? rec.keptAt : 0;
    const data = coerce(rec.data);
    if (!data || !(Date.now() - keptAt < LOST_SAVE_KEEP_MS)) { localStorage.removeItem(lostKey()); return null; }
    return { keptAt, dismissed: rec.dismissed === true, data };
  } catch {
    return null;
  }
}
/** Subscribe to the kept-snapshot record changing (a new race lost, dismissed, restored); returns an unsubscribe fn. */
/**
 * THE ACCEPTED SAVE, AS THE SERVER STORED IT. PUT /api/save answers with its canonical copy; listeners read the
 * server-owned ledgers off it — today `petalsCredited`, which carries achievement Petals the server paid in that very
 * write (server/achievementPetals.js) — so a payout shows in this tab at once rather than on the next load.
 */
const echoListeners = new Set<(stored: Partial<SaveData>) => void>();
export function onSaveAccepted(fn: (stored: Partial<SaveData>) => void): () => void {
  echoListeners.add(fn);
  return () => { echoListeners.delete(fn); };
}

export function onLostSave(fn: (l: LostSave | null) => void): () => void {
  lostListeners.add(fn);
  return () => lostListeners.delete(fn);
}
function writeLostSave(rec: LostSave | null): void {
  try {
    if (rec) localStorage.setItem(lostKey(), JSON.stringify({ ...rec, data: packForWire(rec.data) }));
    else localStorage.removeItem(lostKey());
  } catch { /* storage full / blocked — the notice still fires for this session */ }
  lostListeners.forEach((fn) => fn(rec));
}
/** Hide the notice for the current conflict. The copy is kept until it expires. */
export function dismissLostSave(): void {
  const rec = lostSave();
  if (rec && !rec.dismissed) writeLostSave({ ...rec, dismissed: true });
}
/**
 * "RESTORE MINE": PUT the kept snapshot back as the current save, re-stamped `savedAt = now` so it is the
 * newest copy anywhere — the server accepts it against this tab's base, and `load()`'s newer-of pick
 * prefers it over any older mirror. The record is cleared FIRST, so if this PUT loses a race of its own the
 * 409 branch's fresh record is not wiped afterwards. Callers reload the page afterwards (the in-memory game
 * is still the winner's) and must stop their own pending/unload saves from writing that over it.
 */
export async function restoreLostSave(): Promise<void> {
  const rec = lostSave();
  if (!rec) return;
  writeLostSave(null);
  await apiStore.save({ ...rec.data, savedAt: gameNow() });
}

/**
 * AN ADMIN FIXED THIS ACCOUNT (sim/spectate.ts ACCOUNT_EDITED_CHANNEL): make the cloud copy this device's mirror,
 * so the reload that follows loads the admin's version. Without it a mirror written a moment after the fix — the
 * debounced save of the tab being replaced — is newer by `savedAt`, `load()`'s newer-of pick prefers it, and the
 * fix is written straight back over. Resolves false (the mirror untouched) when the cloud copy can't be read.
 */
export async function adoptCloudSave(): Promise<boolean> {
  try {
    const res = await fetch("/api/save", { headers: await authHeaders() });
    if (!res.ok) return false;
    const data = coerce(await res.json());
    if (!data) return false;
    localStore.save(data);
    return true;
  } catch {
    return false;
  }
}

/**
 * THE BODY SIZE ABOVE WHICH A SAVE MAY NOT ASK FOR `keepalive`, in JSON characters.
 *
 * The Fetch spec's keepalive quota is 64 KiB (65,536 bytes) across all inflight keepalive requests from
 * the page; over it the browser fails the request outright. 60,000 leaves room for the request headers
 * (which count against the quota), for the stats heartbeat's own keepalive POST landing in the same
 * moment, and for multi-byte characters — `body.length` counts UTF-16 units, not bytes.
 *
 * It was added for painted water/terrain (one JSON key per sub-cell); `packForWire` fixed that — a max-size
 * garden painted edge to edge is ~3.5 KB of it now. It STAYS for the rest of the save: the smoke `bigpond`
 * save still PUTs ~39 KB with the pond at ~1.7 KB of that, and plants, decor and progression grow with
 * play. Over this a save still SAVES, as an ordinary fetch the browser may cancel on close — better than a
 * guaranteed failure.
 */
const KEEPALIVE_MAX = 60_000;

/**
 * Cloud save store: talks to the same-origin Node API on Railway. The SERVER is
 * authoritative for plant timestamps (it stamps/clamps `plantedAt` so changing the
 * device clock can't fast-forward growth) — so `load()` returns the server's
 * canonical save, and `save()` sends our blob for the server to validate + persist.
 * Falls back silently to localStorage when the network/API is unavailable so play
 * never blocks. The player id is sent in a header. It also publishes a `SyncStatus`
 * the UI shows, and does a ONE-TIME import of any pre-existing localStorage save the
 * first time the server has none (so progress made before cloud-save isn't lost).
 */
/**
 * Pick the NEWER of two copies of the same player's save, by `savedAt` (game clock ms, stamped in
 * `App.snapshot()`). Returns `remote` on a tie, and whichever one exists when the other is null.
 *
 * A TIE GOES TO THE SERVER on purpose. Equal stamps mean the two are the same snapshot — the mirror is
 * the copy `save()` wrote on its way to that PUT — so the server's is at worst identical and at best has
 * the server's own corrections applied (it re-stamps `plantedAt`, clamps implausible currency). Preferring
 * the local one there would quietly undo those every load.
 *
 * An UNSTAMPED copy (a save written before this field existed) reads as 0 via `coerce`, so it loses to any
 * stamped copy and ties with another unstamped one — which lands on the server, the old behaviour. That is
 * the right migration: the first stamped save on either side ends the ambiguity.
 */
export function newerSave(remote: SaveData | null, local: SaveData | null): SaveData | null {
  if (!remote) return local;
  if (!local) return remote;
  return (local.savedAt ?? 0) > (remote.savedAt ?? 0) ? local : remote;
}

/**
 * THE VERSION THIS TAB IS EDITING: the `savedAt` of the save it loaded, or of its last accepted PUT.
 * Sent on every PUT as `x-save-base` so the server can reject a write built on a copy the row has since
 * moved past (server `saveConflict`). `undefined` until this tab has loaded or written something — a PUT
 * with no base keeps the old last-writer-wins behaviour, which is what a fresh `resetSave()` wants.
 *
 * It is deliberately NOT read back out of the local mirror: the mirror is shared by every tab on this
 * device, so reading it would let a tab that has been asleep for hours inherit another tab's base and
 * present its own stale snapshot as up-to-date — the exact write this whole mechanism exists to stop.
 */
let baseSavedAt: number | undefined;

/**
 * EVERY `savedAt` THIS PAGE HAS SENT (bounded). A 409 whose winning row carries one of these is this page
 * losing a race to ITSELF — two of its own PUTs arriving out of order (a slow request overtaken by the next,
 * or the hide/close flush overtaking one still in flight) — not another device. Nothing was lost: the
 * server kept this page's own newer snapshot. Treating that as a lost race reloaded the tab and announced
 * "another device saved more recently" when there was no other device.
 */
const sentStamps: number[] = [];
function rememberSent(stamp: number | undefined): void {
  if (!stamp) return;
  sentStamps.push(stamp);
  if (sentStamps.length > 64) sentStamps.shift();
}

/**
 * SINGLE-FLIGHT. At most one normal PUT is in flight; a save made meanwhile replaces `queued` and goes out the
 * moment the current one settles, against the base that one established. Parallel PUTs all carried the same
 * `x-save-base` (it only advances on a response), so any two that reached the server out of order made the
 * earlier one a "stale" write. The hide/close flush (`reason: "unload"`) does NOT wait — it has to leave
 * before the page does — and `sentStamps` covers the race it can still cause.
 */
let inFlight: Promise<void> | null = null;
let queued: { data: SaveData; reason: SaveReason } | null = null;

/**
 * RE-ANCHOR THE GAME CLOCK from a save response's `x-server-now` header (the server's `Date.now()`).
 *
 * Every save request already talks to the server, so the sync is free and needs no extra round trip —
 * and these are exactly the moments the clock matters most: a load is when growth since `plantedAt` is
 * first computed, and a save is when `savedAt` is stamped and compared against the stored row. Called on
 * the 404 and the 409 too: both carry the header, and a new player or a losing racer needs an honest
 * clock as much as anyone.
 *
 * A response without the header (an older server, or the stubbed routes in the smoke test's other steps)
 * leaves the offset exactly as it was — `setServerNow` ignores a non-finite reading. See sim/gameClock.
 */
function syncClock(res: Response, sentAt: number): void {
  const h = res.headers.get("x-server-now");
  if (h !== null) setServerNow(Number(h), sentAt);
}

export const apiStore: AsyncSaveStore = {
  async load() {
    // TEST-SERVER "Live save (read-only)" mode: load a snapshot of the admin's LIVE save and DON'T touch
    // any local/test storage. Saving is disabled below, so live + test both stay untouched.
    if (isLiveReadonly()) {
      try {
        const res = await fetch("/api/admin/live-save", { headers: await authHeaders() });
        if (!res.ok) { setStatus("offline"); return null; }
        const data = coerce(await res.json());
        setStatus("saved");           // shown as "saved" but saving is a no-op (read-only)
        return data;                  // NOT mirrored to localStore — keeps the real test save intact
      } catch {
        setStatus("offline");
        return null;
      }
    }
    const sentAt = Date.now();
    try {
      const res = await fetch("/api/save", { headers: await authHeaders() });
      syncClock(res, sentAt);   // before anything reads gameNow() — growth is computed off this load
      if (res.status === 404) {
        // No cloud save yet. If this browser has local progress (from before cloud
        // save existed, or offline play), import it ONCE so it isn't lost.
        const local = localStore.load();
        baseSavedAt = 0;                            // nothing stored — the import below creates the row
        if (local) { void apiStore.save(local); return local; }
        setStatus("saved");
        return null;                                // genuinely new player → fresh game
      }
      if (!res.ok) throw new Error(`load ${res.status}`);
      const json = await res.json();
      const remote = coerce(json);
      // THE ROW VERSION THIS TAB OBSERVED. Always the REMOTE stamp, even when the mirror wins below: the
      // base answers "which stored save did I build on?", and the mirror's own (higher) stamp was never on
      // the server. Using the winner's would make the follow-up PUT claim a base the row has never held.
      baseSavedAt = remote?.savedAt ?? 0;
      // WHICHEVER COPY IS NEWER WINS — not "the server, whenever the GET succeeds".
      //
      // A successful GET does not mean the server has the latest save. `save()` writes the local mirror
      // first and swallows a failed PUT as "offline — the mirror holds it", so a maintenance window (or any
      // stretch of failed PUTs) leaves the mirror AHEAD of the server by every action taken during it.
      // Preferring the server unconditionally and then overwriting the mirror with it deleted all of that
      // on the first refresh after reconnect, with no message and nothing left to recover from.
      const local = localStore.load();
      const data = newerSave(remote, local);
      if (data === local && local) {
        // A newer mirror can still predate a buy-with-Petals debit (a tab left open through the purchase).
        // The server would take the debit off the PUT below anyway, but the game would go on SHOWING the
        // old balance, so apply it here the same way — see `petalsDebited` on SaveData.
        // Likewise the coin → Petal exchange's two ledgers (server/exchange.js).
        let caughtUp = false;
        const owed = (remote?.petalsDebited ?? 0) - (local.petalsDebited ?? 0);
        if (owed > 0) {
          local.petals = Math.max(0, (local.petals ?? 0) - owed);
          local.petalsDebited = remote!.petalsDebited;
          caughtUp = true;
        }
        const coinsOwed = (remote?.coinsDebited ?? 0) - (local.coinsDebited ?? 0);
        if (coinsOwed > 0) {
          local.coins = Math.max(0, local.coins - coinsOwed);
          local.coinsDebited = remote!.coinsDebited;
          caughtUp = true;
        }
        const credit = (remote?.petalsCredited ?? 0) - (local.petalsCredited ?? 0);
        if (credit > 0) {
          local.petals = (local.petals ?? 0) + credit;
          local.petalsCredited = remote!.petalsCredited;
          caughtUp = true;
        }
        // And the REDEMPTION ledger (server/redeem.js): coins, seeds, decor and achievements granted by a code
        // redeemed while this mirror was being written.
        // The mirror then adopts the SERVER's totals outright, even when they are lower (a reset row): an
        // acknowledgement above the server's total would make the next grant read as already applied.
        const serverLedger = coerceGrantLedger(remote?.grantsCredited);
        const granted = ledgerDelta(serverLedger, coerceGrantLedger(local.grantsCredited));
        if (ledgerHasAny(granted)) {
          local.coins += granted.coins;
          local.seeds = addCounts(local.seeds, granted.seeds);
          local.decorOwned = addCounts(local.decorOwned ?? {}, granted.decor);
          local.unlockedAchievements = [...new Set([...(local.unlockedAchievements ?? []), ...granted.achievements])];
        }
        if (remote && JSON.stringify(serverLedger) !== JSON.stringify(coerceGrantLedger(local.grantsCredited))) {
          local.grantsCredited = serverLedger;
          caughtUp = true;
        }
        // And the robot's SHIFT START, which is the server's to stamp (server/index.js arbitrateRobotShift): once
        // the server has seen this mirror's request, its start replaces the mirror's own. Only a request the
        // server has NOT seen yet (asked for while offline) keeps the local stamp until its PUT lands.
        if (remote && (remote.robotShiftReq ?? 0) >= (local.robotShiftReq ?? 0)
          && (remote.robotShiftStart ?? 0) !== (local.robotShiftStart ?? 0)) {
          local.robotShiftReq = remote.robotShiftReq;
          local.robotShiftStart = remote.robotShiftStart;
          caughtUp = true;
        }
        if (caughtUp) localStore.save(local);
        // The mirror won: the server is behind by the unsynced stretch. PUT it back NOW rather than waiting
        // for the next gameplay change — the player may close the tab first, and this load has already
        // decided the mirror is canonical. Deliberately not awaited: boot must not block on the network,
        // and `save()` sets its own status (this one's `setStatus("saved")` below would race it otherwise).
        void apiStore.save(local);
        return local;                               // mirror already holds it — no re-write needed
      }
      if (data) localStore.save(data);              // mirror locally as offline cache
      setStatus("saved");
      return data;
    } catch {
      // THE SERVER COULD NOT SAY WHAT IT HOLDS (offline, a 500/503 in a spike, an expired token). Two rules
      // keep that from costing a player their garden:
      //   • base 0, never "no base": a PUT with no `x-save-base` is last-writer-wins, so the first autosave of
      //     whatever this tab plays would land blind over the stored row. With base 0 the server answers 409
      //     with its copy whenever it holds one, and the newer-wins merge below decides — nothing is lost.
      //   • no local copy → NO GAME, not a fresh one. A fresh save is always the NEWEST copy, so it would win
      //     that merge too: a backer opening the game on a new device during a server blip got the tutorial,
      //     and 400 ms after their first click the blank garden replaced the real one. The loader shows a
      //     "couldn't reach your garden — try again" card instead (ui/GameLoader).
      setStatus("offline");
      baseSavedAt = 0;
      const local = localStore.load();
      if (local) return local;                      // offline fallback: this device's own copy
      throw new SaveUnreachableError();
    }
  },
  async save(data, reason = "normal") {
    // TEST-SERVER "Live save (read-only)" mode: HARD no-op. Nothing is written to live OR to the test
    // save/local mirror — the loaded live snapshot is view-only, and the real test save is preserved.
    if (isLiveReadonly()) { setStatus("saved"); return; }
    // ANOTHER TAB IS PLAYING THIS ACCOUNT (sim/playLock.ts): this tab's game is a stale copy, so it writes
    // nothing — not the cloud row, not the shared mirror — until the player chooses "Play here".
    if (saveBlockedByOtherTab()) return;
    localStore.save(data);                          // always keep a local mirror
    setStatus("saving");
    if (reason === "unload") return sendSave(data, reason);   // leaves now — the page may not outlive a queue
    if (inFlight) { queued = { data, reason }; return inFlight; }   // the running chain sends the newest
    inFlight = (async () => {
      let next: { data: SaveData; reason: SaveReason } | null = { data, reason };
      while (next) {
        await sendSave(next.data, next.reason);
        next = queued; queued = null;
      }
    })().finally(() => { inFlight = null; });
    return inFlight;
  },
};

/** One save to the server, with the conflict handling. `apiStore.save` is the only caller (it serialises). */
async function sendSave(data: SaveData, reason: SaveReason): Promise<void> {
  try {
    const res = await putSave(data, reason);
    if (res.status === 409) {
      // WE LOST A RACE. Another device (or another tab) wrote this row after we read it, so the server
      // refused our blob rather than letting it replace theirs — the stale-tab overwrite this whole
      // mechanism exists to stop. Neither copy is dropped: the 409 body carries the save that beat us; we
      // adopt it and keep ours aside — unless the row is only this page's own earlier write (below).
      //
      // Resolving in favour of one whole snapshot is the honest thing a save file can do — the two
      // copies are alternate histories of one garden, not mergeable field by field, and picking per
      // field would invent a state neither device ever played. When OURS is the loser it is kept on this
      // device for a day (`lostSave`) and the player is told, with a way to take it back — a silent
      // swap under a green "Cloud save" pill was the old behaviour.
      const body = await res.json().catch(() => null) as { data?: unknown } | null;
      const theirs = coerce(body?.data ?? null);
      baseSavedAt = theirs?.savedAt ?? 0;          // their write is the version we now build on
      // NOT A LOSS: the row holds THIS PAGE'S OWN later save (its PUTs crossed in flight), or the very
      // snapshot we are sending (a reload, or a second tab, re-offering what already landed). Nothing of
      // ours is missing from the server, so there is nothing to keep, announce or reload for.
      const theirStamp = theirs?.savedAt ?? 0;
      if (theirs && (data.savedAt ?? 0) <= theirStamp && (theirStamp === (data.savedAt ?? 0) || sentStamps.includes(theirStamp))) {
        setStatus("saved");
        return;
      }
      // RE-SEND ONLY OVER OUR OWN WRITE: the row holds an EARLIER save from this page (its PUTs crossed in
      // flight), so ours supersedes it. NEVER over a copy written by another device or tab, however its
      // `savedAt` compares: that stamp is `gameNow()` at snapshot time, so a stale tab's copy always looks
      // newest — comparing them let a game left open on a laptop silently overwrite the session just played on
      // a phone, and undo an admin's backup restore. ONE retry only: if we lose again, someone else is writing.
      if (theirs && sentStamps.includes(theirStamp) && (data.savedAt ?? 0) > theirStamp) {
        const retry = await putSave(data, reason);
        setStatus(retry.ok ? "saved" : "offline");
        if (retry.ok) baseSavedAt = data.savedAt ?? baseSavedAt;
        return;
      }
      // A 409 with no readable copy: nothing to adopt, so nothing to announce. The mirror still holds ours.
      if (!theirs) { setStatus("offline"); return; }
      // Theirs won — another device or tab wrote it. Adopt it as the local mirror so this tab stops re-offering
      // a snapshot the server has already ruled stale — without this the next save would 409 again, forever.
      // THEN keep ours, in that order: the listener (ui/App) reloads this tab into the mirror, and the notice
      // rides across that reload in localStorage.
      localStore.save(theirs);
      writeLostSave({ keptAt: Date.now(), dismissed: false, data });
      setStatus("saved");
      return;
    }
    if (res.ok) baseSavedAt = Math.max(baseSavedAt ?? 0, data.savedAt ?? 0);   // accepted — the row's version now
    if (res.ok && echoListeners.size) {
      const stored = await res.json().catch(() => null) as Partial<SaveData> | null;
      if (stored && typeof stored === "object") for (const fn of echoListeners) { try { fn(stored); } catch { /* a listener's bug is not a failed save */ } }
    }
    setStatus(res.ok ? "saved" : "offline");
  } catch {
    setStatus("offline");                         // offline — local mirror holds it
  }
}

/** One PUT of the save blob, stamped with the version this tab believes it is editing (`x-save-base`). */
async function putSave(data: SaveData, reason: SaveReason): Promise<Response> {
  const body = JSON.stringify(packForWire(data));
  const sentAt = Date.now();
  rememberSent(data.savedAt);
  const res = await fetch("/api/save", {
    method: "PUT",
    headers: await authHeaders({
      "content-type": "application/json",
      ...(baseSavedAt === undefined ? {} : { "x-save-base": String(baseSavedAt) }),
    }),
    body,
    // KEEPALIVE IS NOT FREE, AND IT IS NOT THE DEFAULT. It exists so the tab-close flush can outlive
    // the document — but the Fetch spec caps the TOTAL inflight keepalive body at 64 KiB, and a browser
    // rejects an over-cap request with a plain network error, indistinguishable here from being offline.
    // Setting it on EVERY save once silently killed cloud save for players with a painted pond. So: ask
    // for it only on the unload flush, and only when the body is comfortably under the cap.
    keepalive: reason === "unload" && body.length < KEEPALIVE_MAX,
  });
  syncClock(res, sentAt);   // every accepted, stale-rejected or errored PUT re-anchors the clock
  return res;
}

/**
 * The store the app uses. Cloud when an API is present (Railway), else local-only.
 * `import.meta.env.VITE_CLOUD_SAVE` is "1" in the Railway build; in a plain static/
 * dev build it's unset → localStore, so nothing changes offline.
 */
// `?.` so the module also loads outside Vite (tsx --test imports it through sim/achievements.ts).
export const cloudSaveEnabled = import.meta.env?.VITE_CLOUD_SAVE === "1";
export const saveStore: SaveStore = cloudSaveEnabled ? apiStore : localStore;

/** Load the save through whichever store is active (always async-friendly). */
/** The cloud save could not be read and this device holds no copy — the loader must NOT start a fresh game
 *  (see `apiStore.load`'s catch). */
export class SaveUnreachableError extends Error {
  constructor() { super("Couldn't reach your saved garden."); this.name = "SaveUnreachableError"; }
}

export async function loadSave(): Promise<SaveData> {
  const loaded = await Promise.resolve(saveStore.load());
  return loaded ?? freshSave();
}

/**
 * RESET this account to a brand-new game. Persists a `freshSave()` through the active store (cloud PUT +
 * local mirror, or local-only) AND clears THIS OWNER's localStorage mirror key so no stale mirror can
 * resurrect old progress on the next load. Only this owner's — another account's mirror on the same browser
 * is not ours to delete, and the legacy key is already retired by the write above. Returns the fresh save. Dev/testing use (the DevMenu "Reset to start" button)
 * — lets us repeatedly wipe the dev account to re-test the new-player walkthrough. Callers typically reload
 * the page afterwards so all in-memory state re-initialises from the fresh save.
 *
 * NOTE: in the test-server "Live save (read-only)" mode the cloud save() is a hard no-op, so a reset there
 * only clears the local mirror — the real live/test saves are untouched (by design).
 */
export async function resetSave(): Promise<SaveData> {
  const fresh = freshSave();
  await Promise.resolve(saveStore.save(fresh));   // cloud PUT (+ local mirror) or local write
  try { localStorage.removeItem(mirrorKey()); } catch { /* storage blocked — non-fatal */ }
  return fresh;
}
